import { useState } from "react";
import { MOCK } from "./MockData";
import { fmt, statusColor } from "./Utils/Utilities";
import { Badge, ActionMenu } from "./SharedUI";
import { downloadOrderPdf } from "./Utils/pdf";

// ─── Status pipeline strip ────────────────────────────────────────────────────
const PIPELINE = ["Pending", "Confirmed", "Processing", "In Transit", "Delivered", "Cancelled"];

const STATUS_FLOW = {
  Pending:    ["Confirmed", "Cancelled"],
  Confirmed:  ["Processing", "Cancelled"],
  Processing: ["In Transit", "Cancelled"],
  "In Transit": ["Delivered"],
};

function StatusPipeline({ orders, active, onSelect }) {
  return (
    <div style={{
      display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap",
    }}>
      {PIPELINE.map((s) => {
        const count   = orders.filter((o) => o.status === s).length;
        const isOn    = active === s;
        const col     = statusColor(s);
        return (
          <div
            key={s}
            onClick={() => onSelect(isOn ? "All" : s)}
            style={{
              flex: "1 1 80px", minWidth: 80,
              background: isOn ? `${col}14` : "var(--surface)",
              border: `1px solid ${isOn ? col : "var(--border)"}`,
              borderTop: `2px solid ${isOn ? col : "var(--border)"}`,
              borderRadius: "var(--r8)", padding: "10px 14px",
              cursor: "pointer", transition: "all .14s",
            }}
            onMouseEnter={e => { if (!isOn) e.currentTarget.style.borderColor = col; }}
            onMouseLeave={e => { if (!isOn) e.currentTarget.style.borderColor = "var(--border)"; }}
          >
            <div style={{
              fontSize: 20, fontWeight: 800, fontFamily: "inherit",
              color: isOn ? col : "var(--text1)", lineHeight: 1, marginBottom: 4,
            }}>{count}</div>
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: ".1em",
              textTransform: "uppercase",
              color: isOn ? col : "var(--text3)",
            }}>{s}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
const SITES = ["Sky Hill", "Moonstone", "Sasol"];

function OrdersPage({ showToast: toast, onSelectOrder, setPage }) {
  const [orders,       setOrders]       = useState(MOCK.orders);
  const [search,       setSearch]       = useState("");
  const [filterStatus, setFilterStatus] = useState("All");

  const filtered = orders.filter((o) => {
    const q = search.toLowerCase();
    const siteName = SITES[orders.indexOf(o) % SITES.length] || "";
    const matchQ = (
      o.id.toLowerCase().includes(q) ||
      siteName.toLowerCase().includes(q) ||
      o.product.toLowerCase().includes(q)
    );
    return matchQ && (filterStatus === "All" || o.status === filterStatus);
  });

  const updateStatus = (order, status) => {
    setOrders((p) => p.map((o) => (o.id === order.id ? { ...o, status } : o)));
    toast(`${order.id} → ${status}`, "s");
  };

  return (
    <div className="page">

      {/* ── Pipeline ── */}
      <StatusPipeline
        orders={orders}
        active={filterStatus}
        onSelect={setFilterStatus}
      />

      <div className="panel">

        {/* ── Toolbar ── */}
        <div className="tbar">
          <div className="tsearch">
            <i className="bi bi-search" style={{ color: "var(--text3)", fontSize: 12 }} />
            <input
              placeholder="Search by ID, site, product…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select
            className="tfilter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            {["All", ...PIPELINE].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>

          <button
            className="btn btn-p"
            onClick={() => setPage?.("create-order")}
          >
            <i className="bi bi-plus-lg" /> New Order
          </button>

          <span
            className="fs11 mono"
            style={{ marginLeft: "auto", color: "var(--text3)" }}
          >
            {filtered.length} of {orders.length} orders
          </span>
        </div>

        {/* ── Table ── */}
        <table className="tbl">
          <thead>
              <tr>
                <th>Order ID</th>
                <th>Site</th>
                <th>Cylinders</th>
                <th>Total</th>
                <th>Order Date</th>
                <th>Delivery</th>
                <th>Driver</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? filtered.map((o, i) => (
              <tr
                key={o.id}
                style={{ cursor: "pointer" }}
                onClick={() => onSelectOrder?.(o)}
              >
                <td>
                  <span className="mono t-flame" style={{ fontWeight: 700, fontSize: 11 }}>
                    {o.id}
                  </span>
                </td>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{SITES[i % SITES.length]}</div>
                </td>
                <td className="mono fs11" style={{ color: "var(--text2)" }}>
                  {o.qty ? fmt.number(o.qty) : "—"} {o.unit || ""}
                </td>
                <td>
                  <span className="mono" style={{ fontWeight: 700, fontSize: 12 }}>
                    {fmt.currency(o.total)}
                  </span>
                </td>
                <td className="mono fs10" style={{ color: "var(--text3)" }}>
                  {o.date ? fmt.date(o.date) : "—"}
                </td>
                <td className="mono fs10" style={{ color: "var(--text3)" }}>
                  {o.delivery ? fmt.date(o.delivery) : "—"}
                </td>
                <td className="fs11" style={{ color: "var(--text2)" }}>
                  {o.driver || <span style={{ color: "var(--text3)" }}>—</span>}
                </td>
                <td><Badge status={o.status} /></td>
                <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                  <ActionMenu
                    items={[
                      ...(STATUS_FLOW[o.status] || []).map((s) => ({
                        icon: <i className="bi bi-arrow-repeat" />,
                        label: `Move to ${s}`,
                        action: () => updateStatus(o, s),
                      })),
                      {
                        icon: <i className="bi bi-clipboard" />,
                        label: "View Details",
                        action: () => onSelectOrder?.(o),
                      },
                      {
                        icon: <i className="bi bi-download" />,
                        label: "Download Invoice",
                        action: () => downloadOrderPdf(o),
                      },
                    ]}
                  />
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={10}>
                  <div className="empty">
                    <div className="empty-ico">
                      <i className="bi bi-box-seam" />
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                      No orders found
                    </div>
                    <div className="fs11 tc3">
                      {search ? "Try a different search term" : "Place your first order to get started"}
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default OrdersPage;